import React from 'react';
import { Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FONT_FAMILY } from '../../theme/createDealSkoutTheme';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.matchMedia) &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useCountUp(target: number) {
  const instant = process.env.NODE_ENV === 'test' || prefersReducedMotion();
  const [value, setValue] = React.useState(instant ? target : 0);

  React.useEffect(() => {
    if (instant) {
      setValue(target);
      return undefined;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / 900);
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [instant, target]);

  return `$${value.toLocaleString('en-US')}`;
}

const LINE =
  'M 28 92 C 62 88 74 86 102 78 C 136 68 150 72 184 56 C 216 40 232 46 268 30 C 300 16 318 20 336 14';

const SAMPLE_CHIPS = ['Sample net $48,500', 'Sample cash $62,000', 'Sample comp $246,000'];

function useCalmMotion() {
  const [calm, setCalm] = React.useState(
    () => process.env.NODE_ENV === 'test' || prefersReducedMotion()
  );

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'test' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setCalm(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return calm;
}

/** Decorative sample trend. Hidden from assistive tech. Not a forecast. */
export const GrowthCurve: React.FC = () => {
  const theme = useTheme();
  const calm = useCalmMotion();
  const dark = theme.palette.mode === 'dark';
  const accent = theme.palette.brand.yellow;
  const navy = theme.palette.brand.navy;
  const label = theme.palette.text.primary;
  const uid = React.useId().replace(/:/g, '');
  const lineEnd = dark ? '#FFFFFF' : accent;
  const candle = dark ? accent : navy;
  const grid = dark ? '#F2F5F9' : navy;
  const gridOpacity = dark ? 0.4 : 0.22;

  return (
    <Box
      className="ds-chart"
      aria-hidden="true"
      sx={{
        mt: 2.5,
        maxWidth: 460,
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: dark ? 'rgba(242,245,249,0.22)' : 'divider',
        background: dark
          ? `linear-gradient(180deg, color-mix(in srgb, ${accent} 28%, ${theme.palette.background.paper}), ${theme.palette.background.paper})`
          : `linear-gradient(180deg, color-mix(in srgb, ${accent} 22%, #ffffff), #ffffff)`,
      }}
    >
      <svg viewBox="0 0 360 128" width="100%" focusable="false" style={{ display: 'block', height: 'auto' }}>
        <defs>
          <linearGradient id={`${uid}-line`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor={dark ? accent : navy} />
            <stop offset="1" stopColor={lineEnd} />
          </linearGradient>
          <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={accent} stopOpacity={dark ? 0.55 : 0.38} />
            <stop offset="1" stopColor={accent} stopOpacity="0" />
          </linearGradient>
          <filter id={`${uid}-glow`} x="-30%" y="-60%" width="160%" height="220%">
            <feGaussianBlur stdDeviation={dark ? 3.2 : 1.6} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {[36, 58, 80].map((y) => (
          <line key={y} x1="16" y1={y} x2="344" y2={y} stroke={grid} strokeOpacity={gridOpacity} strokeWidth="1" />
        ))}
        <text x="16" y="18" fill={label} fontFamily={FONT_FAMILY} fontSize="11" fontWeight="600">
          Illustrative trend
        </text>
        <text x="16" y="120" fill={label} fontFamily={FONT_FAMILY} fontSize="11" fontWeight="600">
          Earlier
        </text>
        <text x="300" y="120" fill={label} fontFamily={FONT_FAMILY} fontSize="11" fontWeight="600">
          Later
        </text>
        <g className="ds-candles" style={{ color: candle }}>
          <rect x="40" y="62" width="8" height="22" rx="1" fill="currentColor" opacity="0.45" />
          <rect x="56" y="52" width="8" height="32" rx="1" fill="currentColor" opacity="0.7" />
          <rect x="72" y="66" width="8" height="18" rx="1" fill="currentColor" opacity="0.4" />
          <rect x="88" y="44" width="8" height="40" rx="1" fill="currentColor" opacity="0.85" />
        </g>
        <path d={`${LINE} L 336 100 L 28 100 Z`} fill={`url(#${uid}-area)`} />
        <path
          d={LINE}
          fill="none"
          stroke={accent}
          strokeWidth={dark ? 8 : 6}
          strokeLinecap="round"
          opacity={dark ? 0.4 : 0.22}
        />
        <path
          className="ds-line"
          d={LINE}
          fill="none"
          stroke={`url(#${uid}-line)`}
          strokeWidth="3.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${uid}-glow)`}
        />
        <g transform={calm ? 'translate(336 14)' : undefined}>
          {!calm && <animateMotion dur="1.6s" fill="freeze" path={LINE} />}
          <circle r="11" fill={accent} opacity={dark ? 0.45 : 0.28} />
          <circle r="4.5" fill={dark ? '#FFFFFF' : navy} stroke={accent} strokeWidth="2" />
        </g>
      </svg>
      <Box className="ds-float" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {SAMPLE_CHIPS.map((labelText) => (
          <Chip
            key={labelText}
            size="small"
            variant="outlined"
            label={labelText}
            sx={{ borderColor: 'brand.yellow', bgcolor: 'background.paper' }}
          />
        ))}
      </Box>
    </Box>
  );
};
